import { useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { Dialog as RDialog, DropdownMenu } from "radix-ui";
import { ChevronDown, LayoutGrid, LogOut, Moon, ShieldAlert, ShieldCheck, Sun, User, X } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { useLive } from "@/live/store";
import type { StreamInfo } from "@/live/store";
import { setThemePref, useResolvedTheme } from "@/lib/theme";
import { formatPps } from "@/lib/format";
import { getConfig } from "@/config";
import { cn } from "@/components/ui/cn";
import { allNavItems, MOBILE_LABEL, MOBILE_PRIMARY, NAV, type NavItem } from "./nav";

const STREAM_TEXT: Record<StreamInfo["status"], string> = {
  idle: "Chưa kết nối",
  connecting: "Đang kết nối",
  live: "Trực tiếp",
  reconnecting: "Đang kết nối lại",
  forbidden: "Không có quyền",
  stopped: "Đã dừng",
};

function streamTone(s: StreamInfo["status"]): string {
  if (s === "live") return "bg-good";
  if (s === "forbidden" || s === "stopped" || s === "idle") return "bg-ink-3";
  return "bg-warning";
}

function StreamIndicator() {
  const live = useLive();
  const rows: [string, StreamInfo][] = [
    ["Lưu lượng", live.trafficStream],
    ["Sự kiện DDoS", live.eventStream],
  ];
  const allLive = rows.every(([, s]) => s.status === "live");
  const anyBad = rows.some(([, s]) => s.status === "reconnecting" || s.status === "connecting");
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        className="flex h-8 items-center gap-2 rounded-lg px-2 text-[13px] text-ink-2 transition-colors hover:bg-surface-2"
        aria-label="Trạng thái kết nối thời gian thực"
      >
        <span
          className={cn("size-2 rounded-full", allLive ? "bg-good" : anyBad ? "omf-pulse bg-warning" : "bg-ink-3")}
          aria-hidden
        />
        <span className="hidden sm:inline">{allLive ? "Trực tiếp" : anyBad ? "Đang kết nối…" : "Ngoại tuyến"}</span>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="omf-menu z-50 w-72 rounded-xl border border-line-strong bg-surface p-3 text-[13px] shadow-pop"
        >
          <p className="mb-2 font-semibold">Kết nối thời gian thực (gRPC-Web)</p>
          {rows.map(([name, s]) => (
            <div key={name} className="flex items-start justify-between gap-3 py-1">
              <span className="text-ink-2">{name}</span>
              <span className="flex flex-col items-end">
                <span className="flex items-center gap-1.5">
                  <span className={cn("size-2 rounded-full", streamTone(s.status))} aria-hidden />
                  {STREAM_TEXT[s.status]}
                </span>
                {s.lastError && s.status !== "live" && (
                  <span className="max-w-44 truncate text-xs text-ink-3" title={s.lastError}>
                    {s.lastError}
                  </span>
                )}
              </span>
            </div>
          ))}
          <p className="mt-2 border-t border-line pt-2 text-xs text-ink-3">
            Tự kết nối lại khi mất kết nối. Dữ liệu cũ được giữ lại trên biểu đồ.
          </p>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function AttackBanner() {
  const live = useLive();
  const active = live.attacks.active();
  if (active.length === 0) return null;
  const peak = Math.max(...active.map((a) => a.startPps ?? 0));
  const first = active[0]!;
  return (
    <Link
      to="/attacks"
      className="flex items-center gap-3 border-b border-critical/40 bg-critical-wash px-4 py-2 text-[13px] text-ink hover:brightness-110 lg:px-5"
      role="alert"
    >
      <ShieldAlert className="size-4 shrink-0 text-critical-ink" aria-hidden />
      <span className="font-semibold text-critical-ink">
        {active.length === 1 ? "Đang bị tấn công DDoS" : `${active.length} cuộc tấn công DDoS đang diễn ra`}
      </span>
      <span className="hidden truncate text-ink-2 sm:inline">
        {first.nodeName} · bộ lọc {first.filterName || "—"}
        {peak > 0 && ` · ${formatPps(peak)} lúc phát hiện`}
      </span>
      <span className="ml-auto shrink-0 font-medium text-critical-ink">Xem chi tiết →</span>
    </Link>
  );
}

function UserMenu() {
  const { me, signOut } = useAuth();
  const navigate = useNavigate();
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        className="flex h-8 items-center gap-2 rounded-lg px-2 text-[13px] text-ink-2 transition-colors hover:bg-surface-2"
        aria-label={`Tài khoản ${me?.discord_username ?? ""}`.trim()}
      >
        <span className="flex size-6 items-center justify-center rounded-full bg-surface-3 text-ink-2">
          <User className="size-3.5" aria-hidden />
        </span>
        <span className="hidden max-w-36 truncate sm:inline">{me?.discord_username ?? "…"}</span>
        <ChevronDown className="hidden size-3.5 sm:block" aria-hidden />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="omf-menu z-50 min-w-52 rounded-xl border border-line-strong bg-surface p-1 text-[13px] shadow-pop"
        >
          <div className="px-2.5 py-2">
            <p className="font-medium">{me?.discord_username}</p>
            <p className="text-xs text-ink-3">
              {me?.is_admin_bypass ? "Quản trị tối cao (toàn quyền)" : `${me?.effective_permissions.length ?? 0} quyền`}
            </p>
          </div>
          <DropdownMenu.Separator className="my-1 h-px bg-line" />
          <DropdownMenu.Item
            className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 transition-colors outline-none data-[highlighted]:bg-surface-2"
            onSelect={() => navigate("/settings")}
          >
            <User className="size-3.5" aria-hidden /> Phiên & cài đặt
          </DropdownMenu.Item>
          <DropdownMenu.Item
            className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 transition-colors text-critical-ink outline-none data-[highlighted]:bg-surface-2"
            onSelect={async () => {
              await signOut();
              navigate("/login", { replace: true });
            }}
          >
            <LogOut className="size-3.5" aria-hidden /> Đăng xuất
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function ThemeToggle() {
  const theme = useResolvedTheme();
  const next = theme === "dark" ? "light" : "dark";
  return (
    <button
      type="button"
      onClick={() => setThemePref(next)}
      className="flex size-8 items-center justify-center rounded-lg text-ink-2 transition-colors hover:bg-surface-2"
      aria-label={next === "light" ? "Chuyển sang giao diện sáng" : "Chuyển sang giao diện tối"}
      title={next === "light" ? "Giao diện sáng" : "Giao diện tối"}
    >
      {theme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
    </button>
  );
}

function Sidebar() {
  const { canAny } = useAuth();
  const live = useLive();
  const activeAttacks = live.attacks.active().length;
  return (
    <nav aria-label="Điều hướng chính" className="flex flex-col gap-5 px-3 py-4">
      {NAV.map((group) => {
        const items = group.items.filter((i) => i.anyOf.length === 0 || canAny(...i.anyOf));
        if (items.length === 0) return null;
        return (
          <div key={group.label}>
            <p className="mb-1.5 px-2 text-[11px] font-semibold tracking-wider text-ink-3 uppercase">{group.label}</p>
            <ul className="flex flex-col gap-0.5">
              {items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === "/"}
                    className={({ isActive }) =>
                      cn(
                        "flex h-8 items-center gap-2.5 rounded-lg px-2 text-[13px] font-medium transition-colors duration-150",
                        isActive ? "bg-accent-wash text-ink" : "text-ink-2 hover:bg-surface-2 hover:text-ink",
                      )
                    }
                  >
                    <item.icon className="size-4 shrink-0" aria-hidden />
                    <span className="truncate">{item.label}</span>
                    {item.to === "/attacks" && activeAttacks > 0 && (
                      <span className="ml-auto rounded-full bg-critical px-1.5 text-[11px] font-semibold text-white">
                        {activeAttacks}
                      </span>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </nav>
  );
}

function isActivePath(pathname: string, to: string): boolean {
  return to === "/" ? pathname === "/" : pathname === to || pathname.startsWith(`${to}/`);
}

function useVisibleNav() {
  const { canAny } = useAuth();
  const visible = (i: NavItem) => i.anyOf.length === 0 || canAny(...i.anyOf);
  const groups = NAV.map((g) => ({ ...g, items: g.items.filter(visible) })).filter((g) => g.items.length > 0);
  const byPath = new Map(allNavItems().filter(visible).map((i) => [i.to, i]));
  const primary = MOBILE_PRIMARY.map((to) => byPath.get(to)).filter((i): i is NavItem => i !== undefined);
  return { groups, primary };
}

/**
 * Phone navigation: a fixed bottom bar with the incident pages, and "Thêm"
 * opening a sheet with every page as a tile grid (no scrolling carousel).
 */
function BottomNav() {
  const { pathname } = useLocation();
  const { groups, primary } = useVisibleNav();
  const [open, setOpen] = useState(false);
  const live = useLive();
  const activeAttacks = live.attacks.active().length;
  const onPrimary = primary.some((i) => isActivePath(pathname, i.to));
  const current = allNavItems().find((i) => isActivePath(pathname, i.to) && i.to !== "/") ?? null;

  const slot = (active: boolean, icon: React.ReactNode, label: string, badge?: number) => (
    <span className="flex flex-col items-center gap-1">
      <span
        className={cn(
          "relative flex h-8 w-14 items-center justify-center rounded-full transition-all duration-200",
          active ? "bg-accent-wash text-accent-text" : "text-ink-3",
        )}
      >
        {icon}
        {badge ? (
          <span className="absolute -top-0.5 right-2 min-w-4 rounded-full bg-critical px-1 text-center text-[10px] leading-4 font-semibold text-white">
            {badge}
          </span>
        ) : null}
      </span>
      <span className={cn("text-[11px] leading-none font-medium", active ? "text-ink" : "text-ink-3")}>{label}</span>
    </span>
  );

  return (
    <>
      <nav
        aria-label="Điều hướng nhanh"
        className="omf-safe-bottom fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface/90 backdrop-blur-md lg:hidden"
      >
        <ul className="mx-auto flex max-w-lg items-stretch justify-around px-1 pt-1.5 pb-1.5">
          {primary.map((item) => {
            const active = isActivePath(pathname, item.to);
            return (
              <li key={item.to} className="flex-1">
                <NavLink to={item.to} end={item.to === "/"} className="flex justify-center py-0.5" aria-current={active ? "page" : undefined}>
                  {slot(
                    active,
                    <item.icon className="size-[18px]" aria-hidden />,
                    MOBILE_LABEL[item.to] ?? item.label,
                    item.to === "/attacks" ? activeAttacks : undefined,
                  )}
                </NavLink>
              </li>
            );
          })}
          <li className="flex-1">
            <button
              type="button"
              className="flex w-full justify-center py-0.5"
              onClick={() => setOpen(true)}
              aria-haspopup="dialog"
              aria-label="Thêm chức năng"
            >
              {slot(!onPrimary && current !== null, <LayoutGrid className="size-[18px]" aria-hidden />, "Thêm")}
            </button>
          </li>
        </ul>
      </nav>

      <RDialog.Root open={open} onOpenChange={setOpen}>
        <RDialog.Portal>
          <RDialog.Overlay className="omf-overlay fixed inset-0 z-40 bg-black/50 backdrop-blur-[2px] lg:hidden" />
          <RDialog.Content className="omf-sheet omf-safe-bottom fixed inset-x-0 bottom-0 z-50 max-h-[85vh] overflow-y-auto rounded-t-3xl border-t border-line-strong bg-surface shadow-pop lg:hidden">
            <div className="sticky top-0 z-[1] bg-surface px-5 pt-2.5 pb-2">
              <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-line-strong" aria-hidden />
              <div className="flex items-center justify-between">
                <RDialog.Title className="text-base font-semibold">Tất cả chức năng</RDialog.Title>
                <RDialog.Close className="rounded-full p-1.5 text-ink-3 transition-colors hover:bg-surface-2" aria-label="Đóng">
                  <X className="size-4" />
                </RDialog.Close>
              </div>
              <RDialog.Description className="sr-only">Chọn một trang để mở</RDialog.Description>
            </div>
            <div className="flex flex-col gap-5 px-4 pt-1 pb-5">
              {groups.map((g) => (
                <section key={g.label}>
                  <p className="mb-2 px-1 text-[11px] font-semibold tracking-wider text-ink-3 uppercase">{g.label}</p>
                  <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                    {g.items.map((item) => {
                      const active = isActivePath(pathname, item.to);
                      return (
                        <li key={item.to}>
                          <NavLink
                            to={item.to}
                            end={item.to === "/"}
                            onClick={() => setOpen(false)}
                            className={cn(
                              "flex h-20 flex-col items-center justify-center gap-2 rounded-2xl border px-1 text-center text-[12px] leading-tight font-medium transition-colors",
                              active
                                ? "border-accent/40 bg-accent-wash text-ink"
                                : "border-line bg-surface-2/60 text-ink-2 active:bg-surface-3",
                            )}
                          >
                            <item.icon className={cn("size-5", active ? "text-accent-text" : "text-ink-3")} aria-hidden />
                            <span className="line-clamp-2">{item.label}</span>
                          </NavLink>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}
            </div>
          </RDialog.Content>
        </RDialog.Portal>
      </RDialog.Root>
    </>
  );
}

export function AppShell() {
  const { pathname } = useLocation();
  const cfg = getConfig();
  return (
    <div className="flex h-full">
      <aside className="hidden w-56 shrink-0 flex-col border-r border-line bg-surface lg:flex">
        <Link to="/" className="flex h-14 items-center gap-2.5 border-b border-line px-5">
          <ShieldCheck className="size-5 text-accent-text" aria-hidden />
          <span className="truncate text-sm font-semibold">{cfg.appName}</span>
        </Link>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <Sidebar />
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center gap-2 border-b border-line bg-surface px-4 lg:px-5">
          <Link to="/" className="flex min-w-0 items-center gap-2 lg:hidden">
            <ShieldCheck className="size-5 shrink-0 text-accent-text" aria-hidden />
            <span className="truncate text-sm font-semibold">{cfg.appName}</span>
          </Link>
          <div className="ml-auto flex items-center gap-1">
            <StreamIndicator />
            <ThemeToggle />
            <UserMenu />
          </div>
        </header>
        <AttackBanner />
        <main className="min-h-0 flex-1 overflow-y-auto">
          <div key={pathname} className="omf-page mx-auto max-w-[1600px] px-4 pt-6 pb-28 lg:px-6 lg:pb-6">
            <Outlet />
          </div>
        </main>
      </div>
      <BottomNav />
    </div>
  );
}
